import React, { useState } from 'react';
import { UserCircle } from '@styled-icons/boxicons-regular';
import { Flex } from 'deprecated-enkel';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { USERS_IMAGE_BASE_URL } from 'constants/url-constants';

import { ImageDragAndDrop } from 'components/ImageDragAndDrop';

import { InlineError } from 'ui/InlineError';
import { TotemButton } from 'ui/Button';

import { handleTextInput, objectIsInObject, validatePhone } from 'utils';

import { TotemLabel, TotemInput } from 'styles';
import {
    SettingsTitleContainerWrapper,
    SettingsTitleContainer,
    SettingsTitle,
    SettingsForm,
    SettingsCard,
    SettingsLeftHalf,
    SettingsRightHalf,
    OptionText,
} from 'styles/pages/settings.styles';

import { analyticsEvents, track } from 'tracking/segment';

import { GetUserQuery, UserFragmentFragment, useUpdateUserMutation } from 'data/__generated__';

type UserProfile = UserFragmentFragment['profile'];

type AccountFormData = {
    firstname: string | undefined;
    lastname: string | undefined;
    phone: string | undefined;
    jobTitle: string | undefined;
};
export const AccountInformation = ({ user }: { user: GetUserQuery['user'] }) => {
    const [updateUser, { loading }] = useUpdateUserMutation();
    const {
        profile: { firstname, lastname, phone, jobTitle, imageUrl },
    } = user;

    const [profileValues, setProfileValues] = useState<Partial<UserProfile>>({});
    const {
        formState: { errors },
        handleSubmit,
        register,
    } = useForm<AccountFormData>({
        reValidateMode: 'onSubmit',
        defaultValues: {
            firstname: firstname ?? undefined,
            lastname: lastname ?? undefined,
            phone: phone ?? undefined,
            jobTitle: jobTitle ?? undefined,
        },
    });

    const handleAccountChange = async (data: AccountFormData) => {
        try {
            const { phone: validatedPhoneNumber } = data;
            const parsedPhone = parsePhoneNumberFromString(validatedPhoneNumber ?? '', 'FR');
            const parsedPhoneNumber = parsedPhone ? parsedPhone.number : '';
            setProfileValues({ ...profileValues, phone: parsedPhoneNumber });

            await updateUser({
                variables: {
                    data: {
                        profile: {
                            ...user.profile,
                            ...profileValues,
                            phone: parsedPhoneNumber,
                        },
                    },
                    _id: user._id,
                },
                refetchQueries: ['GET_USER'],
            });
            track(analyticsEvents.UPDATE_USER);
        } catch (error: any) {
            if (window && window.Log && typeof window.Log.error === 'function') {
                window.Log.error(error);
            }
            throw error;
        }
    };

    const hasAccountChanged = Boolean(
        Object.keys(profileValues).length > 0 &&
            !objectIsInObject(user.profile, profileValues) &&
            user.profile.imageUrl !== USERS_IMAGE_BASE_URL + profileValues?.imageUrl,
    );

    const controller = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleTextInput(e, setProfileValues, profileValues);
    };

    return (
        <SettingsCard>
            <SettingsForm onSubmit={handleSubmit(handleAccountChange)}>
                <SettingsTitleContainerWrapper>
                    <SettingsTitleContainer>
                        <UserCircle />
                        <SettingsTitle>Mon compte</SettingsTitle>
                    </SettingsTitleContainer>
                    <TotemButton
                        $fontSize="1em"
                        $alignSelf="flex-end"
                        type="submit"
                        disabled={!hasAccountChanged}
                        $isLoading={loading}
                    >
                        {loading ? <ClipLoader color="white" size="16px" /> : 'Mettre à jour'}
                    </TotemButton>
                </SettingsTitleContainerWrapper>
                <Flex $flow="row" $wrap="wrap" $alignItems="flex-start">
                    <SettingsLeftHalf $width="232px" $flexShrink={0}>
                        <ImageDragAndDrop
                            imageUrl={imageUrl ?? ''}
                            user={user}
                            label="Photo"
                            values={profileValues}
                            setValues={setProfileValues}
                        />
                    </SettingsLeftHalf>
                    <SettingsRightHalf $width="100%">
                        <TotemLabel>
                            Prénom <InlineError display={errors.firstname} />
                            <TotemInput
                                placeholder="Entrez votre prénom"
                                {...register('firstname', { required: true })}
                                onChange={controller}
                            />
                        </TotemLabel>
                        <TotemLabel>
                            Nom <InlineError display={errors.lastname} />
                            <TotemInput
                                placeholder="Entrez votre nom"
                                {...register('lastname', { required: true })}
                                onChange={controller}
                            />
                        </TotemLabel>
                        <TotemLabel>
                            Poste <InlineError display={errors.jobTitle} />
                            <TotemInput
                                placeholder="Entrez votre poste"
                                {...register('jobTitle', { required: true })}
                                onChange={controller}
                            />
                        </TotemLabel>
                        <TotemLabel>
                            Numéro de téléphone <InlineError display={errors.phone} />
                            <TotemInput
                                placeholder="Entrez votre numéro de téléphone"
                                type="phone"
                                {...register('phone', {
                                    required: true,
                                    validate: (value) =>
                                        validatePhone("Votre numéro de téléphone n'est pas valide.")(value ?? ''),
                                })}
                                onChange={controller}
                            />
                        </TotemLabel>
                        <EmailNotice>
                            Les emails liés aux commandes se règlent par totem : référent et préférences dans « Contact
                            et emails », sur le tableau de bord de chaque totem.
                        </EmailNotice>
                    </SettingsRightHalf>
                </Flex>
            </SettingsForm>
        </SettingsCard>
    );
};

const EmailNotice = styled(OptionText)`
    margin-top: ${({ theme }) => theme.variables.margin}px;
    text-transform: none;
`;
